import YourLibrary from './list';

// Redux
import { useAppDispatch, useAppSelector } from '../../../../store/store';
import {
  LEGACY_COMPOSER_SYNC_ENABLED,
  DISABLE_LEGACY_LIBRARY_FETCHES,
  YOUR_LIBRARY_TTL_MS,
  yourLibraryActions,
} from '../../../../store/slices/yourLibrary';
import { uiActions } from '../../../../store/slices/ui';
import { onboardingActions, selectOnboardingFlag } from '../../../../store/slices/onboarding';
import { whenAppReady } from '../../../../lib/appReadiness';
import { selectAllowLibraryNetwork } from '../../../../store/slices/bootPhase';

// Utils / Interfaces
import { useCallback, useEffect, useMemo, useRef, type FC } from 'react';
import { getFromLocalStorageWithExpiry } from '../../../../utils/localstorage';

interface LibraryProps {}

export const Library: FC<LibraryProps> = () => {
  const globalObj = useMemo(() => (typeof window !== 'undefined' ? (window as any) : (globalThis as any)), []);
  const dispatch = useAppDispatch();
  const authUser = useAppSelector((state) => state.auth.user);
  const accessToken = useAppSelector((state) => state.auth.token);
  const userId = authUser?.id?.trim();
  const user = !!authUser;
  const hasTriggeredAlbumsFetchRef = useRef(false);
  const hasTriggeredIncrementalSyncRef = useRef(false);
  const hasTriggeredWorksFetchRef = useRef(false);
  const hasTriggeredRecordingsFetchRef = useRef(false);
  const albumSyncTimeoutRef = useRef<number | null>(null);
  const {
    myAlbums,
    myArtists,
    myPlaylists,
    myComposers,
    myWorks,
    myRecordings,
    artistsStatus,
    albumsStatus,
    albumsSyncStatus,
    recordingsStatus,
    worksStatus,
    playlistsStatus,
    lastFetchedAt,
    importStatus,
  } = useAppSelector((state) => state.yourLibrary);
  const allowLibraryNetwork = useAppSelector(selectAllowLibraryNetwork);
  const importOnboardingFlag = useAppSelector((state) => selectOnboardingFlag(state, 'importOnboarding'));
  const onboardingStatus = useAppSelector((state) => state.onboarding.status);
  const disableLegacyFetches = DISABLE_LEGACY_LIBRARY_FETCHES;
  const isStale = useCallback(
    (timestamp: number | null) => !timestamp || Date.now() - timestamp > YOUR_LIBRARY_TTL_MS,
    []
  );

  const hasLibraryData = useMemo(
    () =>
      myAlbums.length +
        myArtists.length +
        myPlaylists.length +
        myComposers.length +
        myWorks.length +
        myRecordings.length >
      0,
    [
      myAlbums.length,
      myArtists.length,
      myPlaylists.length,
      myComposers.length,
      myWorks.length,
      myRecordings.length,
    ]
  );

  useEffect(() => {
    const currentUserId = authUser?.id?.trim();
    if (!currentUserId) return;
    if (importStatus === 'pending') return;

    const flagSeen = Boolean(importOnboardingFlag?.seenAt);
    const haveFlag = importOnboardingFlag !== null;
    const fetchFinished = onboardingStatus === 'succeeded' || onboardingStatus === 'failed';
    if (!haveFlag && !fetchFinished) return;
    if (flagSeen) return;

    dispatch(uiActions.openImportOnboarding());
    dispatch(
      onboardingActions.markOnboardingSeen({
        key: 'importOnboarding',
        version: importOnboardingFlag?.version ?? 1,
      })
    );
  }, [authUser?.id, dispatch, hasLibraryData, importOnboardingFlag, importStatus, onboardingStatus]);

  useEffect(() => {
    if (!allowLibraryNetwork || disableLegacyFetches) return;

    let cancelled = false;
    const runWhenReady = (fn: () => void) => {
      void whenAppReady().then(() => {
        if (!cancelled) {
          fn();
        }
      });
    };

    if (!user) return;

    if (process.env.NODE_ENV === 'development') {
      console.info('[RECFETCH] Library effect start', {
        recordingsStatus,
        lastFetchedAt: lastFetchedAt.recordings,
        recordingsCount: myRecordings.length,
      });
    }

    const albumsStale = isStale(lastFetchedAt.albums);
    if (albumsStale) {
      hasTriggeredIncrementalSyncRef.current = false;
    }
    const needsInitialAlbumLoad =
      myAlbums.length === 0 &&
      (albumsStatus === 'idle' || albumsStatus === 'failed');
    const shouldRefreshAlbums =
      albumsStale &&
      (albumsStatus !== 'loading' || !hasTriggeredAlbumsFetchRef.current);
    const canFetchAlbums = needsInitialAlbumLoad || shouldRefreshAlbums;

    if (canFetchAlbums) {
      hasTriggeredAlbumsFetchRef.current = true;
      dispatch(yourLibraryActions.fetchMyAlbums());
    }

    const shouldFetchArtists =
      (myArtists.length === 0 && artistsStatus === 'idle') ||
      (artistsStatus !== 'loading' && isStale(lastFetchedAt.artists));

    if (shouldFetchArtists) {
      dispatch(yourLibraryActions.fetchMyArtists());
    }

    // Only fetch playlists if not already loading, and we need to fetch (empty or stale)
    const playlistsStale = isStale(lastFetchedAt.playlists);
    const needsInitialPlaylistsLoad = myPlaylists.length === 0 && playlistsStatus === 'idle';
    const shouldRefreshPlaylists = playlistsStale && playlistsStatus !== 'loading';
    if (needsInitialPlaylistsLoad || shouldRefreshPlaylists) {
      dispatch(yourLibraryActions.fetchMyPlaylists());
    }

    if (LEGACY_COMPOSER_SYNC_ENABLED && (myComposers.length === 0 || isStale(lastFetchedAt.composers))) {
      dispatch(yourLibraryActions.fetchMyComposers());
    }

    // Only fetch works if not already loading, and we need to fetch (empty or stale)
    const worksStale = isStale(lastFetchedAt.works);
    const shouldFetchWorks = 
      worksStatus !== 'loading' && 
      (myWorks.length === 0 || worksStale) && 
      !hasTriggeredWorksFetchRef.current;
    
    const worksFetchInFlight = Boolean(globalObj.__libraryWorksFetchInFlight);
    if (shouldFetchWorks && !worksFetchInFlight) {
      hasTriggeredWorksFetchRef.current = true;
      globalObj.__libraryWorksFetchInFlight = true;
      runWhenReady(() => {
        dispatch(yourLibraryActions.fetchMyWorks());
      });
    }

    const shouldFetchRecordings =
      (myRecordings.length === 0 && recordingsStatus === 'idle') ||
      (recordingsStatus !== 'loading' && isStale(lastFetchedAt.recordings));

    const recordingsFetchInFlight = Boolean(globalObj.__libraryRecordingsFetchInFlight);
    if (shouldFetchRecordings && !recordingsFetchInFlight && !hasTriggeredRecordingsFetchRef.current) {
      if (process.env.NODE_ENV === 'development') {
        console.info('[RECFETCH] Library dispatch fetchMyRecordings', {
          shouldFetchRecordings,
          recordingsStatus,
          lastFetchedAt: lastFetchedAt.recordings,
          recordingsCount: myRecordings.length,
        });
      }
      hasTriggeredRecordingsFetchRef.current = true;
      globalObj.__libraryRecordingsFetchInFlight = true;
      runWhenReady(() => {
        dispatch(yourLibraryActions.fetchMyRecordings());
      });
    }

    const hasFreshAlbums = myAlbums.length > 0 && !albumsStale;
    const resolvedAccessToken =
      [
        accessToken,
        getFromLocalStorageWithExpiry('access_token') as string | undefined,
        getFromLocalStorageWithExpiry('public_access_token') as string | undefined,
      ].find((token) => typeof token === 'string' && token.trim().length > 0) ?? null;

    const isStaleSnapshotLoading = albumsStale && albumsStatus === 'loading';
    const canSyncAlbums =
      typeof userId === 'string' &&
      typeof resolvedAccessToken === 'string' &&
      resolvedAccessToken.trim().length > 0 &&
      albumsSyncStatus !== 'pending' &&
      albumsStatus === 'succeeded' &&
      !isStaleSnapshotLoading &&
      hasFreshAlbums &&
      !hasTriggeredIncrementalSyncRef.current;

    if (canSyncAlbums) {
      if (process.env.NODE_ENV === 'development') {
        console.info('[extracall] triggering incremental album sync');
      }
      // Delay the sync a bit so it does not compete with page-critical requests
      hasTriggeredIncrementalSyncRef.current = true;
      if (albumSyncTimeoutRef.current !== null) {
        window.clearTimeout(albumSyncTimeoutRef.current);
        albumSyncTimeoutRef.current = null;
      }
      albumSyncTimeoutRef.current = window.setTimeout(() => {
        albumSyncTimeoutRef.current = null;
        dispatch(
          yourLibraryActions.syncMyAlbums({
            userId,
            accessToken: resolvedAccessToken.trim(),
          })
        );
      }, 3000);
    }

    return () => {
      cancelled = true;
    };
  }, [
    user,
    dispatch,
    userId,
    accessToken,
    myAlbums.length,
    myArtists.length,
    myPlaylists.length,
    myComposers.length,
    myRecordings.length,
    myWorks.length,
    artistsStatus,
    albumsStatus,
    albumsSyncStatus,
    recordingsStatus,
    worksStatus,
    playlistsStatus,
    lastFetchedAt.albums,
    lastFetchedAt.artists,
    lastFetchedAt.playlists,
    lastFetchedAt.composers,
    lastFetchedAt.works,
    lastFetchedAt.recordings,
    isStale,
    globalObj,
    allowLibraryNetwork,
    disableLegacyFetches,
  ]);

  useEffect(
    () => () => {
      if (albumSyncTimeoutRef.current !== null) {
        window.clearTimeout(albumSyncTimeoutRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (albumsStatus === 'succeeded' || albumsStatus === 'failed') {
      hasTriggeredAlbumsFetchRef.current = false;
    }
    // Reset works fetch ref when works fetch completes (succeeded or failed)
    if (worksStatus === 'succeeded' || worksStatus === 'failed') {
      hasTriggeredWorksFetchRef.current = false;
      if (globalObj.__libraryWorksFetchInFlight) {
        globalObj.__libraryWorksFetchInFlight = false;
      }
    }
    if (recordingsStatus === 'succeeded' || recordingsStatus === 'failed') {
      hasTriggeredRecordingsFetchRef.current = false;
      if (globalObj.__libraryRecordingsFetchInFlight) {
        globalObj.__libraryRecordingsFetchInFlight = false;
      }
    }
  }, [albumsStatus, worksStatus, recordingsStatus, globalObj]);

  return (
    <div style={{ height: '100%' }}>
      <YourLibrary />
    </div>
  );
};

export default Library;
